import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  ArrowLeft, 
  Save,
  Plus,
  X,
  HelpCircle,
  CheckCircle,
  XCircle,
  Upload,
  FileText,
  ChevronDown,
  Download,
  Image as ImageIcon
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'

const AddTest = () => {
  const navigate = useNavigate()
  const { contentId } = useParams()
  
  const [homeContent, setHomeContent] = useState(null)
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [showAddOptions, setShowAddOptions] = useState(false)
  const [inputMode, setInputMode] = useState('manual') // 'manual' or 'excel'
  
  // Test metadata from form
  const [testMetadata, setTestMetadata] = useState({
    testName: '',
    startDate: '',
    endDate: '',
    duration: 60, // Duration in minutes, default 60
    marks: 0, // Total marks for the test
    testId: null // Store the test ID for updates
  })
  const [questions, setQuestions] = useState([{
    id: 1,
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    explanation: '',
    questionType: 'text', // 'text' or 'image'
    questionImage: null,
    questionImagePreview: null
  }])
  
  const { getHomeContents, addTestQuestions, getTestQuestions, updateTestMetadata, updateTestWithQuestions } = useFirebase()

  useEffect(() => {
    loadHomeContent()

    // Read query parameters for test metadata and editing state
    const urlParams = new URLSearchParams(window.location.search)
    const editTestName = urlParams.get('edit')
    const testName = urlParams.get('testName')
    const startDate = urlParams.get('startDate')
    const endDate = urlParams.get('endDate')
    const duration = urlParams.get('duration')
    const marks = urlParams.get('marks')

    // If we are editing an existing test, load its existing questions
    if (editTestName) {
      loadExistingQuestions()
    } else {
      // Creating a brand-new test: ensure we start with a fresh empty question set
      setQuestions([{
        id: 1,
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
        explanation: '',
        questionType: 'text',
        questionImage: null,
        questionImagePreview: null
      }])
      setTestMetadata(prev => ({
        ...prev,
        testId: null
      }))
    }

    if (testName || startDate || endDate || duration || marks) {
      setTestMetadata(prev => ({
        ...prev,
        testName: testName || prev.testName || '',
        startDate: startDate || prev.startDate || '',
        endDate: endDate || prev.endDate || '',
        duration: duration || prev.duration || 60,
        marks: marks ? parseInt(marks) : prev.marks || 0
      }))
    }
  }, [contentId])

  const loadExistingQuestions = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const editTestName = urlParams.get('edit')
      
      if (editTestName) {
        // Load questions for specific test
        const existingQuestions = await getTestQuestions(contentId)
        const specificTest = existingQuestions.find(test => 
          (test.testName || test.title || test.testTitle) === editTestName
        )
        
        if (specificTest) {
          // Format dates for HTML date inputs (YYYY-MM-DD format)
          const formatDateForInput = (dateValue) => {
            if (!dateValue) return ''
            try {
              let date
              if (dateValue instanceof Date) {
                date = dateValue
              } else if (dateValue.toDate && typeof dateValue.toDate === 'function') {
                date = dateValue.toDate()
              } else {
                date = new Date(dateValue)
              }
              
              if (isNaN(date.getTime())) return ''
              
              // Format as YYYY-MM-DD for HTML date input
              return date.toISOString().split('T')[0]
            } catch (error) {
              console.error('Error formatting date:', error)
              return ''
            }
          }

          // Get URL parameters to override test data if provided
          const urlParams = new URLSearchParams(window.location.search)
          const urlStartDate = urlParams.get('startDate')
          const urlEndDate = urlParams.get('endDate')
          const urlDuration = urlParams.get('duration')
          const urlMarks = urlParams.get('marks')

          // Update test metadata - URL parameters take priority over stored data
          setTestMetadata({
            testName: specificTest.testName || specificTest.title || editTestName,
            startDate: urlStartDate || formatDateForInput(specificTest.startDate),
            endDate: urlEndDate || formatDateForInput(specificTest.endDate),
            duration: urlDuration ? parseInt(urlDuration) : (specificTest.duration || 60),
            marks: urlMarks ? parseInt(urlMarks) : (specificTest.marks || 0),
            testId: specificTest.id // Store the test ID for updates
          })
          
          // Load existing questions for this test
          const formattedQuestions = specificTest.questions?.map((q, index) => {
            const formatted = {
              id: index + 1,
              question: q.question,
              options: q.options,
              correctAnswer: q.correctAnswer,
              explanation: q.explanation,
              questionType: q.questionType || 'text',
              questionImage: null,
              questionImagePreview: q.questionImageURL || null
            }
            console.log(`🐛 DEBUG: Loading Q${index + 1} from test - questionImageURL = "${q.questionImageURL}", questionType = "${q.questionType}"`)
            return formatted
          }) || []
          
          if (formattedQuestions.length > 0) {
            setQuestions(formattedQuestions)
          }
        }
      }
    } catch (error) {
      console.error('Error loading existing questions:', error)
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAddOptions && !event.target.closest('.relative')) {
        setShowAddOptions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showAddOptions])

  const loadHomeContent = async () => {
    try {
      setInitialLoading(true)
      const contents = await getHomeContents()
      const foundContent = contents.find(c => c.id === contentId)
      if (foundContent) {
        setHomeContent(foundContent)
      } else {
        toast.error('Home content not found')
        navigate(`/tests-management/${contentId}`)
      }
    } catch (error) {
      console.error('Error loading home content:', error)
      toast.error('Failed to load home content')
      navigate(`/tests-management/${contentId}`)
    } finally {
      setInitialLoading(false)
    }
  }

  const switchToManualMode = () => {
    setInputMode('manual')
    setQuestions([{
      id: 1,
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: '',
      questionType: 'text',
      questionImage: null,
      questionImagePreview: null
    }])
    setShowAddOptions(false)
  }

  const addNewQuestion = () => {
    const maxId = questions.length > 0 ? Math.max(...questions.map(q => q.id)) : 0
    const newQuestion = {
      id: maxId + 1,
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      explanation: '',
      questionType: 'text',
      questionImage: null,
      questionImagePreview: null
    }
    setQuestions([...questions, newQuestion])
    setShowAddOptions(false)
  }

  const handleExcelUpload = (event) => {
    const file = event.target.files[0]
    if (file) {
      parseExcelFile(file)
    }
    setShowAddOptions(false)
    // Reset the input value so the same file can be selected again
    event.target.value = ''
  }

  const parseExcelFile = (file) => {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
        
        // Parse the Excel data
        const parsedQuestions = parseQuestionsFromExcel(jsonData)
        
        if (parsedQuestions.length > 0) {
          // Check if we have only the default empty question
          const hasOnlyEmptyQuestion = questions.length === 1 && 
            questions[0].question === '' && 
            questions[0].options.every(opt => opt === '') && 
            questions[0].explanation === ''

          let currentMaxId
          let existingQuestions
          
          if (hasOnlyEmptyQuestion) {
            // Replace the empty default question
            currentMaxId = 0
            existingQuestions = []
          } else {
            // Append to existing questions
            currentMaxId = questions.length > 0 ? Math.max(...questions.map(q => q.id)) : 0
            existingQuestions = questions
          }
          
          const newQuestions = parsedQuestions.map((q, index) => ({
            id: currentMaxId + index + 1,
            question: q.question,
            options: q.options,
            correctAnswer: q.correctAnswer,
            explanation: q.explanation || '',
            questionType: 'text',
            questionImage: null,
            questionImagePreview: null
          }))
          
          // Combine existing questions with new Excel questions
          setQuestions(() => {
            const combinedQuestions = [...existingQuestions, ...newQuestions]
            
            // Show success message after state update
            setTimeout(() => {
              toast.success(`Successfully imported ${parsedQuestions.length} questions from Excel. Total questions: ${combinedQuestions.length}`)
            }, 100)
            
            return combinedQuestions
          })
          
          // Set input mode based on whether we replaced or appended
          if (hasOnlyEmptyQuestion) {
            setInputMode('excel') // Pure Excel mode since we replaced the empty question
          } else {
            setInputMode('mixed') // Mixed mode since we appended to existing questions
          }
        } else {
          toast.error('No valid questions found in the Excel file')
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error)
        toast.error('Failed to parse Excel file. Please check the format.')
      }
    }
    
    reader.onerror = () => {
      toast.error('Failed to read the Excel file')
    }
    
    reader.readAsArrayBuffer(file)
  }

  // Parse Excel rows into questions with robust correct-answer mapping
  const parseQuestionsFromExcel = (data) => {
    const parsed = []
    for (let i = 1; i < data.length; i++) {
      const row = data[i]
      if (!row || row.length < 5) continue
      const question = row[0]?.toString().trim()
      const optionA = row[1]?.toString().trim()
      const optionB = row[2]?.toString().trim()
      const optionC = row[3]?.toString().trim()
      const optionD = row[4]?.toString().trim()
      const correctRaw = row[5]
      const explanation = row[6]?.toString().trim() || ''
      if (question && optionA && optionB && optionC && optionD) {
        const options = [optionA, optionB, optionC, optionD]
        const normalize = (s) => (s || '')
          .toString()
          .normalize('NFKD')
          .replace(/\u00A0/g, ' ')
          .replace(/\s+/g, ' ')
          .replace(/[^\p{L}\p{N}\s]/gu, '')
          .trim()
          .toLowerCase()

        const valRaw = (correctRaw ?? '').toString()
        const val = valRaw.trim()
        const upper = val.toUpperCase()
        let idx = -1

        // 1) Letter forms
        if (['A','B','C','D'].includes(upper)) {
          idx = 'ABCD'.indexOf(upper)
        }
        // 2) Labeled forms: Option A-D
        if (idx < 0) {
          const m = upper.match(/^(?:ANSWER\s*[:\-]?\s*)?OPTION\s+([ABCD])$/)
          if (m) idx = 'ABCD'.indexOf(m[1])
        }
        // 3) Numeric forms
        if (idx < 0 && /^[1-4]$/.test(upper)) idx = parseInt(upper, 10) - 1
        if (idx < 0 && /^[0-3]$/.test(upper)) idx = parseInt(upper, 10)

        // 4) Exact text match (trim/case-insensitive)
        if (idx < 0) {
          const exactIdx = options.findIndex(o => (o || '').toString().trim().toLowerCase() === val.toLowerCase())
          if (exactIdx >= 0) idx = exactIdx
        }

        // 5) Normalized text match (remove punctuation/extra spaces/unicode)
        if (idx < 0) {
          const normVal = normalize(valRaw)
          const normIdx = options.findIndex(o => normalize(o) === normVal)
          if (normIdx >= 0) idx = normIdx
        }

        // 6) Fuzzy contains/startsWith (normalized)
        if (idx < 0) {
          const normVal = normalize(valRaw)
          const containsIdx = options.findIndex(o => normalize(o).includes(normVal) || normVal.includes(normalize(o)))
          if (containsIdx >= 0) idx = containsIdx
        }

        // Default to A if still not matched
        if (idx < 0) idx = 0
        idx = Math.max(0, Math.min(3, idx))
        const result = { question, options, correctAnswer: idx, explanation }
        try {
          console.log('[ExcelImport] Row', i + 1, 'question:', question, 'correctRaw:', correctRaw, 'mappedIndex:', idx, 'mappedOption:', options[idx])
        } catch {}
        parsed.push(result)
      }
    }
    return parsed
  }

  // Download a sample Excel template for question import
  const downloadExcelTemplate = () => {
    const templateData = [
      ['Question', 'Option A', 'Option B', 'Option C', 'Option D', 'Correct Answer', 'Explanation'],
      [
        'Who is the founder of modern nursing?',
        'Clara Barton',
        'Florence Nightingale',
        'Mary Seacole',
        'Mother Teresa',
        'Florence Nightingale',
        'Provide a short explanation for the correct answer.'
      ],
      [
        'What is the normal pulse rate of adults?',
        '60–100 bpm',
        '40–50 bpm',
        '100–120 bpm',
        '30–40 bpm',
        'A',
        'Normal adult pulse ranges between 60–100 bpm.'
      ]
    ]

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(templateData)
    ws['!cols'] = [
      { wch: 60 }, { wch: 24 }, { wch: 24 }, { wch: 24 }, { wch: 24 }, { wch: 20 }, { wch: 60 }
    ]
    XLSX.utils.book_append_sheet(wb, ws, 'Questions Template')
    XLSX.writeFile(wb, 'nursing_questions_template.xlsx')
    toast.success('Excel template downloaded successfully!')
  }

  const removeQuestion = (questionId) => {
    if (questions.length > 1) {
      setQuestions(questions.filter(q => q.id !== questionId))
    } else {
      toast.error('At least one question is required')
    }
  }

  const updateQuestion = (questionId, field, value) => {
    setQuestions(questions.map(q => 
      q.id === questionId ? { ...q, [field]: value } : q
    ))
  }

  const updateOption = (questionId, optionIndex, value) => {
    setQuestions(questions.map(q => 
      q.id === questionId 
        ? { ...q, options: q.options.map((opt, idx) => idx === optionIndex ? value : opt) }
        : q
    ))
  }

  const setCorrectAnswer = (questionId, optionIndex) => {
    setQuestions(questions.map(q => 
      q.id === questionId ? { ...q, correctAnswer: optionIndex } : q
    ))
  }

  const setQuestionType = (questionId, type) => {
    setQuestions(questions.map(q => 
      q.id === questionId ? { ...q, questionType: type } : q
    ))
  }

  const handleQuestionImageChange = (questionId, file) => {
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file')
        return
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB')
        return
      }
      
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setQuestions(questions.map(q => 
          q.id === questionId ? { ...q, questionImage: file, questionImagePreview: reader.result } : q
        ))
      }
      reader.readAsDataURL(file)
    }
  }

  const removeQuestionImage = (questionId) => {
    setQuestions(questions.map(q => 
      q.id === questionId ? { ...q, questionImage: null, questionImagePreview: null } : q
    ))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate questions
    for (const question of questions) {
      if (!question.question.trim()) {
        toast.error('Please fill in all question texts')
        return
      }
      
      if (question.options.some(opt => !opt.trim())) {
        toast.error('Please fill in all answer options')
        return
      }
    }

    try {
      setLoading(true)
      
      const urlParams = new URLSearchParams(window.location.search)
      const editTestName = urlParams.get('edit')
      
      // Prepare questions data for Firebase
      const questionsData = {
        questions: questions.map(q => ({
          question: q.question.trim(),
          options: q.options.map(opt => opt.trim()),
          correctAnswer: q.correctAnswer,
          explanation: q.explanation.trim(),
          questionType: q.questionType || 'text',
          questionImage: q.questionImage || null, // Include File object, will be handled specially in Firebase functions
          questionImagePreview: q.questionImagePreview || null // Preserve existing image URL
        })),
        source: 'manual'
      }

      // DEBUG: Log questionImagePreview values for each question
      console.log('🐛 DEBUG AddTest - Questions before sending to Firebase:')
      questionsData.questions.forEach((q, idx) => {
        const hasImage = q.questionImage !== null
        const hasPreview = q.questionImagePreview !== null && q.questionImagePreview !== ''
        const previewValue = q.questionImagePreview ? (q.questionImagePreview.substring(0, 50) + '...') : q.questionImagePreview
        console.log(`  Q${idx + 1}: type="${q.questionType}", hasImage=${hasImage}, hasPreview=${hasPreview}, preview="${previewValue}"`)
      })

      if (editTestName && testMetadata.testName) {
        // Update existing test with new metadata and questions
        const updatedData = {
          ...questionsData,
          testName: testMetadata.testName,
          startDate: testMetadata.startDate,
          endDate: testMetadata.endDate,
          duration: testMetadata.duration,
          marks: testMetadata.marks
        }
        
        if (testMetadata.testId) {
          // Use new structure-aware update (preferred method)
          await updateTestWithQuestions(contentId, testMetadata.testId, updatedData)
          console.log('✅ Used structure-aware update for test:', testMetadata.testId)
        } else {
          // Fallback to name-based update (will need to find test by name first)
          const existingTests = await getTestQuestions(contentId)
          const existingTest = existingTests.find(test => 
            (test.testName || test.title || test.testTitle) === editTestName
          )
          
          if (existingTest) {
            await updateTestWithQuestions(contentId, existingTest.id, updatedData)
            console.log('✅ Used name-based lookup then structure-aware update for test:', existingTest.id)
          } else {
            throw new Error(`Test "${editTestName}" not found`)
          }
        }
        
        toast.success(`Successfully updated "${testMetadata.testName}" with ${questions.length} questions!`)
      } else {
        // Create new test (fallback to old behavior)
        const testData = {
          contentTitle: homeContent?.title,
          testName: testMetadata.testName || `Test for ${homeContent?.title}`,
          startDate: testMetadata.startDate,
          endDate: testMetadata.endDate,
          duration: testMetadata.duration,
          marks: testMetadata.marks,
          ...questionsData,
          createdBy: 'admin@nursingexam.com'
        }
        await addTestQuestions(contentId, testData)
        toast.success(`Successfully saved ${questions.length} test questions!`)
      }
      
      navigate(`/tests-management/${contentId}`)
    } catch (error) {
      console.error('Error saving test:', error)
      toast.error(`Failed to save test questions: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!homeContent) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Home content not found</h3>
        <button onClick={() => navigate(`/tests-management/${contentId}`)} className="btn-primary">
          Back to Test Management
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/tests-management/${contentId}`)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add Test Questions</h1>
            <div className="flex items-center space-x-3">
              <p className="text-sm text-gray-600">For: {homeContent.title}</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                inputMode === 'manual' ? 'bg-blue-100 text-blue-800' : 
                inputMode === 'mixed' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
              }`}>
                {inputMode === 'manual' ? 'Manual Entry' : 
                 inputMode === 'mixed' ? 'Manual + Excel' : 'Excel Import'}
              </span>
            </div>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowAddOptions(!showAddOptions)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Question</span>
            <ChevronDown className="h-4 w-4" />
          </button>
          
          {showAddOptions && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
              <div className="py-2">
                <button
                  onClick={addNewQuestion}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Plus className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Add New Question</div>
                    <div className="text-sm text-gray-600">Add another question manually</div>
                  </div>
                </button>
                
                {(inputMode === 'excel' || inputMode === 'mixed') && (
                  <button
                    onClick={switchToManualMode}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3"
                  >
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <FileText className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Start Fresh (Manual)</div>
                      <div className="text-sm text-gray-600">Clear all and start manual entry</div>
                    </div>
                  </button>
                )}
                
                <div className="border-t border-gray-100">
                  <label className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 cursor-pointer">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Upload className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Excel Upload</div>
                      <div className="text-sm text-gray-600">Upload questions from Excel file</div>
                    </div>
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleExcelUpload}
                      className="hidden"
                    />
                  </label>
                  
                  
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Test Metadata Section */}
        {(testMetadata.testName || testMetadata.startDate || testMetadata.endDate) && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-purple-600" />
                Test Information
              </h2>
            </div>
            <div className="card-body space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test Name
                  </label>
                  <input
                    type="text"
                    value={testMetadata.testName}
                    onChange={(e) => setTestMetadata(prev => ({ ...prev, testName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Test name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={testMetadata.startDate}
                    onChange={(e) => setTestMetadata(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={testMetadata.endDate}
                    onChange={(e) => setTestMetadata(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="600"
                    value={testMetadata.duration}
                    onChange={(e) => setTestMetadata(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Enter duration in minutes"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Questions Section */}
        {questions.map((question, questionIndex) => (
          <div key={question.id} className="card">
            <div className="card-header flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <HelpCircle className="h-5 w-5 mr-2 text-blue-600" />
                Question {questionIndex + 1}
              </h3>
              {questions.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeQuestion(question.id)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title="Remove Question"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="card-body space-y-4">
              {/* Question Type Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Type *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {/* Text Question */}
                  <button
                    type="button"
                    onClick={() => setQuestionType(question.id, 'text')}
                    className={`flex items-center justify-center space-x-2 p-4 border-2 rounded-lg transition-all ${
                      question.questionType === 'text'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <FileText className="h-5 w-5" />
                    <span className="font-medium">Text Question</span>
                  </button>
                  
                  {/* Image Question */}
                  <button
                    type="button"
                    onClick={() => setQuestionType(question.id, 'image')}
                    className={`flex items-center justify-center space-x-2 p-4 border-2 rounded-lg transition-all ${
                      question.questionType === 'image'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <ImageIcon className="h-5 w-5" />
                    <span className="font-medium">Image Question</span>
                  </button>
                </div>
              </div>

              {/* Question Image Upload (only for image type) */}
              {question.questionType === 'image' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Image *
                  </label>
                  {question.questionImagePreview ? (
                    <div className="relative">
                      <img
                        src={question.questionImagePreview}
                        alt="Question preview"
                        className="w-full max-h-64 object-contain rounded-lg border-2 border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => removeQuestionImage(question.id)}
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all block">
                      <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 mb-1">Click to upload question image</p>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleQuestionImageChange(question.id, e.target.files[0])}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              )}

              {/* Question Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Text *
                </label>
                <textarea
                  value={question.question}
                  onChange={(e) => updateQuestion(question.id, 'question', e.target.value)}
                  className="input w-full"
                  rows={3}
                  placeholder="Enter your question here..."
                  required
                />
              </div>

              {/* Answer Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Answer Options *
                </label>
                <div className="space-y-3">
                  {question.options.map((option, optionIndex) => (
                    <div key={optionIndex} className="flex items-center space-x-3">
                      <button
                        type="button"
                        onClick={() => setCorrectAnswer(question.id, optionIndex)}
                        className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          question.correctAnswer === optionIndex
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-gray-300 hover:border-green-400'
                        }`}
                        title={question.correctAnswer === optionIndex ? 'Correct Answer' : 'Mark as Correct'}
                      >
                        {question.correctAnswer === optionIndex && (
                          <CheckCircle className="h-4 w-4" />
                        )}
                      </button>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => updateOption(question.id, optionIndex, e.target.value)}
                          className="input w-full"
                          placeholder={`Option ${String.fromCharCode(65 + optionIndex)}`}
                          required
                        />
                      </div>
                      <span className="text-sm text-gray-500 w-8">
                        {String.fromCharCode(65 + optionIndex)}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Click the circle next to an option to mark it as the correct answer
                </p>
              </div>

              {/* Explanation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Explanation (Optional)
                </label>
                <textarea
                  value={question.explanation}
                  onChange={(e) => updateQuestion(question.id, 'explanation', e.target.value)}
                  className="input w-full"
                  rows={2}
                  placeholder="Explain why this is the correct answer..."
                />
              </div>
            </div>
          </div>
        ))}

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate(`/tests-management/${contentId}`)}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center space-x-2"
          >
            {loading ? (
              <LoadingSpinner size="small" />
            ) : (
              <>
                <Save className="h-5 w-5" />
                <span>Save Test Questions</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AddTest
